import './style.scss';
import { initialize_select2 } from '../../scripts/initialize-select2';

function getFileFieldLabel(input) {
  const field = input.closest('.rebredg-form-field');
  const explicitLabel = field?.querySelector('.rebredg-form-field-label')?.textContent?.trim()
    || input.closest('label')?.textContent?.trim()
    || input.getAttribute('aria-label')?.trim();

  if (explicitLabel) {
    const cleanedLabel = explicitLabel
      .replace(/\s+/g, ' ')
      .split(/\s+[–—-]\s+/)[0]
      .trim();

    if (cleanedLabel) {
      return cleanedLabel;
    }
  }

  const normalizedName = (input.name || '')
    .replace(/[\[\]_-]+/g, ' ')
    .trim()
    .toLowerCase();

  if (/\b(cv|resume)\b/.test(normalizedName)) {
    return 'Your CV';
  }

  if (/\b(additional|attachment|attachments|files?)\b/.test(normalizedName)) {
    return 'Additional Files';
  }

  const fallback = normalizedName
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .replace(/\bCv\b/g, 'CV');

  return fallback || 'Choose a file';
}

function initializeFileAttachments(block) {
  block.querySelectorAll('.careers-page-form input[type="file"]').forEach((input) => {
    const control = input.closest('.wpcf7-form-control-wrap') || input.parentElement;

    if (!control || input.dataset.fileAttachmentInitialized === 'true') {
      return;
    }

    const defaultLabel = getFileFieldLabel(input);
    const visual = document.createElement('span');
    const fileName = document.createElement('span');

    control.classList.add('careers-file-attachment');
    input.classList.add('careers-file-attachment-input');

    visual.className = 'careers-file-attachment-ui';
    visual.setAttribute('aria-hidden', 'true');

    fileName.className = 'careers-file-attachment-name';
    visual.append(fileName);
    control.appendChild(visual);

    const syncFileName = () => {
      const files = Array.from(input.files || []);

      if (!files.length) {
        fileName.textContent = defaultLabel;
        control.classList.remove('has-file');
        return;
      }

      fileName.textContent = files.length === 1
        ? files[0].name
        : `${files[0].name} +${files.length - 1}`;

      control.classList.add('has-file');
    };

    input.addEventListener('change', syncFileName);
    syncFileName();
    input.dataset.fileAttachmentInitialized = 'true';
  });
}

export function careers_page(root = document) {
  root.querySelectorAll('[data-component="careers-page"]').forEach((block) => {
    if (block.dataset.careersInitialized === 'true') return;

    initialize_select2(block);
    initializeFileAttachments(block);

    const { Swiper, SwiperModules = {} } = window.rebredgVendors || {};
    const slider = block.querySelector('[data-careers-swiper]');
    const previous = block.querySelector('[data-careers-previous]');
    const next = block.querySelector('[data-careers-next]');
    const pagination = block.querySelector('[data-careers-pagination]');
    const titleField = block.querySelector('[data-careers-title-field]');
    const defaultView = block.querySelector('[data-careers-default]');
    const thankYou = block.querySelector('[data-careers-thank-you]');
    const thankYouHeading = block.querySelector('[data-careers-thank-you-heading]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const readMoreDuration = reducedMotion ? 0 : 420;
    let careersSwiper = null;
    let heightAnimationFrame = 0;

    const syncRoleFields = () => {
      if (!titleField) return;

      const activeSlide = careersSwiper?.slides?.[careersSwiper.activeIndex]
        || slider?.querySelector('.swiper-slide-active')
        || slider?.querySelector('.swiper-slide');
      const showTitleField = activeSlide?.dataset.showTitleField === '1';

      titleField.hidden = !showTitleField;
      titleField.querySelectorAll('input, select, textarea, button').forEach((control) => {
        control.disabled = !showTitleField;
      });
    };

    const syncSwiperHeight = () => {
      if (!careersSwiper) return;

      if (heightAnimationFrame) window.cancelAnimationFrame(heightAnimationFrame);

      if (reducedMotion) {
        careersSwiper.updateAutoHeight(0);
        return;
      }

      const startedAt = performance.now();
      const update = (now) => {
        careersSwiper.updateAutoHeight(0);
        heightAnimationFrame = now - startedAt < readMoreDuration
          ? window.requestAnimationFrame(update)
          : 0;
      };

      heightAnimationFrame = window.requestAnimationFrame(update);
    };

    block.querySelectorAll('[data-careers-read-more]').forEach((button) => {
      const slide = button.closest('.careers-page-slide');
      const description = slide?.querySelector('[data-careers-description]');
      const label = button.querySelector('[data-careers-read-more-label]');

      if (!description || !label) return;

      const collapsedHeight = Number.parseFloat(
        window.getComputedStyle(description).getPropertyValue('--careers-description-collapsed-height'),
      ) || 103;

      if (description.scrollHeight <= collapsedHeight + 1) {
        description.style.maxHeight = 'none';
        description.classList.add('is-unclipped');
        button.remove();
        return;
      }

      description.style.maxHeight = `${collapsedHeight}px`;

      button.addEventListener('click', () => {
        const nextExpanded = button.getAttribute('aria-expanded') !== 'true';

        button.setAttribute('aria-expanded', String(nextExpanded));
        label.textContent = nextExpanded ? button.dataset.lessLabel : button.dataset.moreLabel;
        description.classList.toggle('is-expanded', nextExpanded);

        if (nextExpanded) {
          description.style.maxHeight = `${description.scrollHeight}px`;
        } else {
          window.requestAnimationFrame(() => {
            description.style.maxHeight = `${collapsedHeight}px`;
          });
        }

        syncSwiperHeight();
      });
    });

    if (Swiper && slider) {
      const modules = [
        SwiperModules.EffectFade,
        SwiperModules.Navigation,
        SwiperModules.Pagination,
        SwiperModules.Keyboard,
        SwiperModules.A11y,
      ].filter(Boolean);
      const hasMultipleRoles = slider.querySelectorAll('.swiper-slide').length > 1;

      careersSwiper = new Swiper(slider, {
        modules,
        slidesPerView: 1,
        effect: 'fade',
        fadeEffect: {
          crossFade: true,
        },
        speed: reducedMotion ? 0 : 550,
        autoHeight: true,
        allowTouchMove: hasMultipleRoles,
        grabCursor: hasMultipleRoles,
        rewind: hasMultipleRoles,
        watchOverflow: true,
        navigation: previous && next ? { prevEl: previous, nextEl: next } : false,
        pagination: pagination ? { el: pagination, clickable: true } : false,
        keyboard: { enabled: hasMultipleRoles, onlyInViewport: true },
        a11y: {
          enabled: true,
          prevSlideMessage: 'Previous role',
          nextSlideMessage: 'Next role',
          paginationBulletMessage: 'Go to role {{index}}',
        },
      });

      careersSwiper.on('slideChange', syncRoleFields);
      careersSwiper.updateAutoHeight(0);
    }

    syncRoleFields();

    if (thankYou) {
      block.addEventListener('wpcf7mailsent', (event) => {
        if (!(event.target instanceof Element) || !block.contains(event.target)) {
          return;
        }

        if (defaultView) {
          defaultView.hidden = true;
        }

        thankYou.hidden = false;
        block.classList.add('is-submitted');
        block.dataset.careersState = 'success';

        const blockTop = block.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: blockTop,
          behavior: reducedMotion ? 'auto' : 'smooth',
        });

        window.setTimeout(() => {
          thankYouHeading?.focus({ preventScroll: true });
        }, reducedMotion ? 0 : 550);
      });
    }

    block.dataset.careersInitialized = 'true';
  });
}
